import React, { useState } from 'react';
import { Box, Container, Toolbar } from '@mui/material';
import Header from '../components/Header/Header';
import Sidebar, { drawerWidth } from '../components/Sidebar/Sidebar';
import Footer from '../components/Footer';
import { WelcomeOverlayRoot } from '../components/WelcomeOverlay';
import { EnrollmentWelcomeRoot } from '../components/EnrollmentWelcomeDialog';

const collapsedDrawerWidth = 60;
/**
 * Shared dashboard chrome: opaque canvas, sidebar, navbar, scrollable main, footer.
 * Ambient shell retired in EF A3 (DR-1) — canvas comes from theme.palette.background.default.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - page content
 * @param {string} props.brandPath - navigate target when clicking the StackTrack brand
 * @param {Array<{ id: string, title: string, initiallyExpanded?: boolean, items: unknown[] }>} props.sections - sidebar groups
 * @param {false | 'xs' | 'sm' | 'md' | 'lg' | 'xl'} [props.contentMaxWidth='lg'] - main content width cap; `false` uses full available width
 */
const BaseDashboardLayout = ({ children, brandPath, sections, contentMaxWidth = 'lg' }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const currentDrawerWidth = sidebarCollapsed ? collapsedDrawerWidth : drawerWidth;

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        bgcolor: 'background.default',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flex: 1,
          minHeight: '100vh',
          width: '100%',
        }}
      >
        <WelcomeOverlayRoot />
        <EnrollmentWelcomeRoot />
        <Header onDrawerToggle={handleDrawerToggle} sidebarCollapsed={sidebarCollapsed} onSidebarToggle={handleSidebarToggle} />
        <Sidebar
          mobileOpen={mobileOpen}
          onDrawerToggle={handleDrawerToggle}
          brandPath={brandPath}
          sections={sections}
          collapsed={sidebarCollapsed}
          onCollapseToggle={handleSidebarToggle}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            margin: 0,
            p: 0,
            width: { xs: '100%', sm: `calc(100% - ${currentDrawerWidth}px)` },
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default',
            transition: (theme) =>
              theme.transitions.create(['width', 'background-color'], {
                duration: theme.transitions.duration.short,
              }),
          }}
        >
          <Toolbar />
          <Container
            maxWidth={contentMaxWidth}
            disableGutters={contentMaxWidth === false}
            sx={{
              flexGrow: 1,
              width: '100%',
              py: { xs: 3, md: 4 },
              px: contentMaxWidth === false ? { xs: 2, sm: 3, lg: 4, xl: 5 } : { xs: 2, sm: 3 },
              mt: 0,
              mb: 0,
            }}
          >
            {children}
          </Container>
          <Footer />
        </Box>
      </Box>
    </Box>
  );
};

export default BaseDashboardLayout;
